import { t } from "@lingui/macro";
import { CircleNotch, FileDoc, FileJs, FilePdf } from "@phosphor-icons/react";
import { buttonVariants, Card, CardContent, CardDescription, CardTitle } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { saveAs } from "file-saver";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { useExportDocx } from "@/client/services/resume/export-docx";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { generatePdfFromArtboard } from "@/client/utils/pdf/from-artboard";

import { SectionIcon } from "../shared/section-icon";

const onJsonExport = () => {
  const { resume } = useResumeStore.getState();
  const filename = `reactive_resume-${resume.id}.json`;
  const resumeJSON = JSON.stringify(resume.data, null, 2);

  saveAs(new Blob([resumeJSON], { type: "application/json" }), filename);
};

const openInNewTab = (url: string) => {
  const win = window.open(url, "_blank");
  if (win) {
    win.focus();
    return true;
  }

  return false;
};

export const ExportSection = () => {
  const { toast } = useToast();
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const format = useResumeStore((state) => state.resume.data.metadata.page.format);
  const title = useResumeStore((state) => state.resume.title);

  const { exportDocx, loading: docxLoading } = useExportDocx();

  const [pdfLoading, setPdfLoading] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  const onPdfExport = async () => {
    if (!frameRef) {
      toast({
        variant: "error",
        title: t`Resume preview is not ready yet.`,
        description: t`Please wait a moment and try again.`,
      });
      return;
    }

    try {
      setPdfLoading(true);
      setProgress({ current: 0, total: 0 });

      const blob = await generatePdfFromArtboard({
        frame: frameRef,
        format,
        title,
        onProgress: ({ current, total }) => {
          setProgress({ current, total });
        },
      });

      const trimmedTitle = (title as string | undefined)?.trim() ?? "";
      const filename = `${trimmedTitle || "resume"}.pdf`;
      saveAs(blob, filename);

      toast({
        variant: "success",
        title: t`PDF ready`,
        description: t`We saved a pixel-perfect copy of your resume.`,
      });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Failed to generate PDF`,
        description: error instanceof Error ? error.message : t`Unknown error`,
      });
    } finally {
      setPdfLoading(false);
      setProgress(null);
    }
  };

  const onDocxExport = async () => {
    try {
      const { resume } = useResumeStore.getState();
      const { url } = await exportDocx({ id: resume.id });

      const opened = openInNewTab(url);
      if (!opened) {
        try {
          const response = await axios.get<ArrayBuffer>(url, { responseType: "arraybuffer" });
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
          const filename = `${resume.title || "resume"}.docx`;
          saveAs(blob, filename);
        } catch {
          window.location.href = url;
        }
      }
    } catch (error) {
      toast({
        variant: "error",
        title: t`Failed to generate DOCX`,
        description: error instanceof Error ? error.message : t`Unknown error`,
      });
    }
  };

  const pdfDescription = (() => {
    if (progress && progress.total > 0) {
      if (progress.current < progress.total) {
        const currentPage = progress.current + 1;
        const totalPages = progress.total;
        return t`Rendering page ${currentPage} of ${totalPages}...`;
      }

      return t`Almost done... wrapping up your PDF.`;
    }

    return t`Download a PDF that matches exactly what you see in the editor.`;
  })();

  return (
    <section id="export" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="export" size={18} name={t`Export`} />
          <h2 className="line-clamp-1 text-2xl font-bold lg:text-3xl">{t`Export`}</h2>
        </div>
      </header>

      <main className="grid gap-y-4">
        <Card
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
          )}
          onClick={onJsonExport}
        >
          <FileJs size={22} />
          <CardContent className="flex-1">
            <CardTitle className="text-sm">{t`JSON`}</CardTitle>
            <CardDescription className="font-normal">
              {t`Download a JSON snapshot of your resume. This file can be used to import your resume in the future, or can even be shared with others to collaborate.`}
            </CardDescription>
          </CardContent>
        </Card>

        <Card
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
            pdfLoading && "pointer-events-none cursor-progress opacity-75",
          )}
          onClick={onPdfExport}
        >
          {pdfLoading ? <CircleNotch size={22} className="animate-spin" /> : <FilePdf size={22} />}

          <CardContent className="flex-1">
            <CardTitle className="text-sm">{t`PDF`}</CardTitle>
            <CardDescription className="font-normal">{pdfDescription}</CardDescription>
          </CardContent>
        </Card>

        <Card
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
            docxLoading && "pointer-events-none cursor-progress opacity-75",
          )}
          onClick={onDocxExport}
        >
          {docxLoading ? <CircleNotch size={22} className="animate-spin" /> : <FileDoc size={22} />}

          <CardContent className="flex-1">
            <CardTitle className="text-sm">{t`DOCX`}</CardTitle>
            <CardDescription className="font-normal">
              {t`Download a DOCX document of your resume. This file can be edited in Microsoft Word or other word processors and shared with recruiters.`}
            </CardDescription>
          </CardContent>
        </Card>
      </main>
    </section>
  );
};
